import { useCallback, useMemo } from "react";
import { Box } from "../../../ui";
import ieFormLogic from "./ieFormLogic.json";
import SmartSuggestionUU, {
  UploadedDocumentInfo,
} from "../uuUi/smartSuggestionUU";
import DateSolutionUU from "../uuUi/dateSolutionUU";
import JsonHelpDialog from "../../JsonHelpDialog";
import { useState } from "react";

interface UuFieldLogic {
  question: string;
  ieFormFieldName: string;
  evidenceTitle?: string;
  evidenceGuidance?: string;
  documentNote?: string;
  // Date input fields
  requiresBenefitStartDate?: boolean;
  dateLabel?: string;
  dateContextText?: string;
  ieFieldFaqs?: { question: string; answer: string }[];
  startDateFaqs?: { question: string; answer: string }[];
}

interface IeFormLogic {
  formName?: string;
  fields: UuFieldLogic[];
}

function isUuFieldLogic(value: unknown): value is UuFieldLogic {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.ieFormFieldName === "string" && typeof v.question === "string"
  );
}

function isIeFormLogic(value: unknown): value is IeFormLogic {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  if (!Array.isArray(v.fields)) return false;
  return v.fields.every(isUuFieldLogic);
}

function normalizeKey(key: string): string {
  return key.trim().toLowerCase();
}

function getLastSegment(path: string): string {
  if (!path) return path;
  const parts = path.split(".");
  return parts[parts.length - 1] || path;
}

export interface UuViewerProps {
  headerTitle?: string;
  /** Field name to match against ieFormLogic.json */
  fieldName: string;
  /** Optional label for fallback title */
  label?: string | null;
  /** Callback when help icon is clicked */
  onOpenHelp?: () => void;
  /** Callback when benefit start date is changed */
  onDateChange?: (month: string, year: string) => void;
  /** Initial month for date picker (e.g., "January") */
  initialMonth?: string;
  /** Initial year for date picker (e.g., "2025") */
  initialYear?: string;
  /** Whether the date input is disabled */
  dateDisabled?: boolean;
  /** Error state for date input */
  dateError?: boolean;
  /** Error message for date input */
  dateErrorText?: string;
  /**
   * Override to control date picker visibility.
   * When undefined, uses JSON's requiresBenefitStartDate.
   * When explicitly true/false, overrides the JSON setting.
   */
  showDatePicker?: boolean;
  /** Whether to show the document upload section */
  showUpload?: boolean;
  /** Existing UU document metadata */
  uploadedDocument?: UploadedDocumentInfo | null;
  /** Upload handler for SmartSuggestion */
  onDocumentUpload?: (file: File, description?: string) => Promise<void> | void;
  /** Delete handler for SmartSuggestion */
  onDocumentDelete?: () => Promise<void> | void;
  /** View handler for SmartSuggestion */
  onDocumentView?: () => Promise<void> | void;
  /** Upload spinner state */
  documentUploading?: boolean;
  /** Callback when description changes (debounced auto-save) */
  onDescriptionChange?: (description: string) => void;
}

/**
 * UuViewer
 * Renders contextual guidance for specific IE fields based on ieFormLogic.json.
 * Uses SmartSuggestionUU for evidence display and DateSolutionUU for date input.
 * Returns null when no matching guidance exists for the provided field.
 */
export default function UuViewer({
  headerTitle,
  fieldName,
  label,
  onOpenHelp,
  onDateChange,
  initialMonth = "",
  initialYear = "",
  dateDisabled = false,
  dateError = false,
  dateErrorText = "",
  showDatePicker: showDatePickerProp,
  showUpload = true,
  uploadedDocument = null,
  onDocumentUpload,
  onDocumentDelete,
  onDocumentView,
  documentUploading = false,
  onDescriptionChange,
}: UuViewerProps) {
  const [faqDialog, setFaqDialog] = useState<{
    faqs: { question: string; answer: string }[];
    title: string;
    description?: string;
  } | null>(null);
  const handleUpload = useCallback(
    (file: File, description?: string) => {
      if (!onDocumentUpload) {
        return;
      }
      void Promise.resolve(onDocumentUpload(file, description));
    },
    [onDocumentUpload]
  );
  const handleDelete = useCallback(() => {
    if (!onDocumentDelete) {
      return;
    }
    void Promise.resolve(onDocumentDelete());
  }, [onDocumentDelete]);

  const handleView = useCallback(
    (_document: UploadedDocumentInfo) => {
      if (!onDocumentView) {
        return;
      }
      void Promise.resolve(onDocumentView());
    },
    [onDocumentView]
  );
  const logicByKey = useMemo(() => {
    const raw: unknown = ieFormLogic as unknown;
    if (!isIeFormLogic(raw)) {
      return new Map<string, UuFieldLogic>();
    }
    const map = new Map<string, UuFieldLogic>();
    raw.fields.forEach((f) => {
      const key = normalizeKey(f.ieFormFieldName);
      if (!map.has(key)) {
        map.set(key, f);
      }
    });
    return map;
  }, []);

  const match = useMemo(() => {
    if (!fieldName) return undefined;
    // Try exact match first
    const exact = logicByKey.get(normalizeKey(fieldName));
    if (exact) return exact;
    // Fallback to last segment match (for nested fields like group.universalCredit)
    const lastSeg = getLastSegment(fieldName);
    return logicByKey.get(normalizeKey(lastSeg));
  }, [fieldName, logicByKey]);

  // Transform ieFormLogic data → SmartSuggestionUU props
  const title = useMemo(() => {
    if (!match) return "";
    return (
      match.evidenceTitle?.trim() || `Evidence for ${label || match.question}`
    );
  }, [match, label]);

  const evidenceFaqs = match?.ieFieldFaqs ?? [];
  const startDateFaqs = match?.startDateFaqs ?? [];

  if (!match) {
    return null;
  }

  // Use prop if explicitly provided, otherwise fall back to JSON config
  const showDatePicker =
    showDatePickerProp ?? match.requiresBenefitStartDate === true;

  const handleOpenEvidenceHelp = useCallback(() => {
    if (evidenceFaqs.length > 0) {
      setFaqDialog({
        faqs: evidenceFaqs,
        title:
          label || match.evidenceTitle || match.question || "Help & FAQs",
      });
      return;
    }
    if (onOpenHelp) onOpenHelp();
  }, [evidenceFaqs, label, match.evidenceTitle, match.question, onOpenHelp]);

  const handleOpenStartDateHelp = useCallback(() => {
    if (startDateFaqs.length > 0) {
      setFaqDialog({
        faqs: startDateFaqs,
        title: match.dateLabel || "Benefit start date help",
      });
      return;
    }
    if (onOpenHelp) onOpenHelp();
  }, [match.dateLabel, onOpenHelp, startDateFaqs]);

  const handleCloseFaq = useCallback(() => setFaqDialog(null), []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1.5 }}>
      {/* Evidence Requirements Card */}
      <SmartSuggestionUU
        headerTitle={headerTitle ?? "Smart Suggestion"}
        title={title}
        evidenceDescription={match.evidenceGuidance}
        {...(match.documentNote && { documentMustInclude: match.documentNote })}
        helpText="Help & FAQs"
        onHelpClick={handleOpenEvidenceHelp}
        showUpload={showUpload && Boolean(onDocumentUpload)}
        uploadedDocument={uploadedDocument ?? null}
        isUploading={documentUploading}
        {...(onDocumentUpload && { onUpload: handleUpload })}
        {...(onDocumentDelete && { onDelete: handleDelete })}
        {...(onDocumentView && { onView: handleView })}
        {...(onDescriptionChange && { onDescriptionChange })}
      />

      {/* Benefit Start Date Picker */}
      {showDatePicker && (
        <DateSolutionUU
          {...(match.dateLabel && { label: match.dateLabel })}
          {...(match.dateContextText && { contextText: match.dateContextText })}
          helpTooltip="Click for more information about finding your benefit start date"
          onHelpClick={handleOpenStartDateHelp}
          {...(onDateChange && { onChange: onDateChange })}
          initialMonth={initialMonth}
          initialYear={initialYear}
          disabled={dateDisabled}
          error={dateError}
          errorText={dateErrorText}
          sx={{ mt: 0 }}
        />
      )}

      <JsonHelpDialog
        open={Boolean(faqDialog)}
        onClose={handleCloseFaq}
        title={faqDialog?.title || "Help & FAQs"}
        {...(faqDialog?.description !== undefined && {
          description: faqDialog.description,
        })}
        faqs={faqDialog?.faqs || []}
      />
    </Box>
  );
}
